import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { colorizeWindowApi, getTaskStatusApi, fetchResultAsBlobUrl } from "../api";
import { ITaskStatus } from "../types";
import { urlToFile, buildResultUrl } from "../utils/imageHelpers";
import { IQuestionOption } from "@/features/assistant/types";

const POLL_INTERVAL_MS = 1000;
const DEBOUNCE_MS = 300;

interface ColorizeState {
  isApplying: boolean;
  colorizedUrl: string | null;
  lastTaskStatus: ITaskStatus | null;
  error: string | null;
}

const pollUntilDone = async (
  taskId: string,
  isCancelled: () => boolean,
): Promise<string> => {
  while (true) {
    if (isCancelled()) throw new Error("cancelled");
    const status = await getTaskStatusApi(taskId);
    if (status.state === "SUCCESS") {
      if (!status.result_url) throw new Error("Task succeeded but result_url is missing");
      return buildResultUrl(status.result_url);
    }
    if (status.state === "FAILURE") throw new Error(status.error || `Task ${taskId} failed`);
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
};

/**
 * Colorizes a window template's frame with a hex color before blending.
 * Runs before DBR and grill in the pipeline.
 *
 * Single composed key + single effect — same pattern as useDeepBottomRail's
 * dbrKey and useGrillBlend's grillKey. An earlier two-effect design (one
 * effect to "reset" on selectedProduct change, a separate effect to do the
 * real work) raced itself: the reset effect ran first in the same commit and
 * pre-synced its "last applied" marker to the CURRENT props, so the second
 * effect's "did anything change?" check always found a match and skipped the
 * real colorize call — switching products silently kept the previous
 * product's blended image. Folding productId directly into one key and
 * using one effect removes that race entirely: there's only one place that
 * decides whether to re-run, and it's a plain key-string comparison.
 *
 * @param templateUrl - Original product thumbnail URL
 * @param colorHex    - Hex color from profile color answer (e.g. "#8B4513"), null = skip
 * @param enabled     - When false, seeds refs so no call fires until a real change occurs
 */
export const useColorizeWindow = ({
  colorHex,
  enabled = true,
  templateUrl,
  selectedProduct,
  forceKey,
}: {
  templateUrl: string | null;
  colorHex: string | null;
  enabled: boolean;
  selectedProduct?: IQuestionOption;
  // Bump this to force a genuine re-run (e.g. when grill resolves to "None"
  // and the de-grilled base needs to be freshly verified) — folded into the
  // key below, so it's a real re-trigger rather than just clearing state.
  forceKey?: string | number;
}) => {
  const [state, setState] = useState<ColorizeState>({
    isApplying: false,
    colorizedUrl: null,
    lastTaskStatus: null,
    error: null,
  });

  // Product identity (not just its thumbnail URL) so switching to a product
  // that happens to reuse the same thumbnail still forces a genuine re-run —
  // matches useDeepBottomRail's dbrKey, which already includes optionId.
  const productId = selectedProduct?.optionId ?? "";

  const colorizeKey = useMemo(() => {
    if (!colorHex || !templateUrl) return "";
    return `colorize:${templateUrl}:${colorHex}:${productId}:${forceKey ?? ""}`;
  }, [colorHex, templateUrl, productId, forceKey]);

  const lastAppliedKeyRef = useRef("");
  const currentBlobUrlRef = useRef<string | null>(null);
  const prevEnabledRef = useRef(false);

  const colorizeMutation = useMutation({
    mutationFn: (data: { window_image: File; color: string }) => colorizeWindowApi(data),
  });

  useEffect(() => {
    if (!enabled) {
      lastAppliedKeyRef.current = colorizeKey;
      prevEnabledRef.current = false;
      return;
    }

    // enabled just transitioned false→true: clear the ref so the full
    // pipeline re-runs even if the key hasn't changed (e.g. color-only
    // change with an existing colorize result).
    const justEnabled = !prevEnabledRef.current;
    prevEnabledRef.current = true;

    // No color or no template — pass through original as-is
    if (!colorizeKey) {
      lastAppliedKeyRef.current = colorizeKey;
      setState((prev) => ({ ...prev, colorizedUrl: templateUrl ?? null }));
      return;
    }

    // Nothing changed — skip (but not on the first enable transition)
    if (!justEnabled && lastAppliedKeyRef.current === colorizeKey) return;

    let cancelled = false;

    const timer = setTimeout(async () => {
      setState((prev) => ({ ...prev, isApplying: true, error: null }));

      try {
        const windowFile = await urlToFile(templateUrl!, "window.png");
        if (!windowFile || windowFile.size === 0) {
          setState((prev) => ({
            ...prev,
            isApplying: false,
            colorizedUrl: templateUrl,
          }));
          return;
        }

        if (cancelled) return;

        const submission = await colorizeMutation.mutateAsync({
          window_image: windowFile,
          color: colorHex!,
        });

        if (cancelled) return;

        const resultUrl = await pollUntilDone(
          submission.task_id,
          () => cancelled,
        );

        if (!cancelled) {
          const blobUrl = await fetchResultAsBlobUrl(resultUrl);
          if (!cancelled) {
            if (currentBlobUrlRef.current?.startsWith("blob:")) {
              URL.revokeObjectURL(currentBlobUrlRef.current);
            }
            currentBlobUrlRef.current = blobUrl;
            lastAppliedKeyRef.current = colorizeKey;
            setState({
              isApplying: false,
              colorizedUrl: blobUrl,
              lastTaskStatus: {
                task_id: submission.task_id,
                state: "SUCCESS",
                result_url: resultUrl,
              },
              error: null,
            });
          }
        }
      } catch (error) {
        if (!cancelled) {
          const msg = error instanceof Error ? error.message : String(error);
          if (msg !== "cancelled") {
            // Fall back to original template on failure
            setState({
              isApplying: false,
              colorizedUrl: templateUrl,
              lastTaskStatus: null,
              error: msg,
            });
          }
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, colorizeKey, templateUrl, colorHex]);

  const reset = useCallback(() => {
    if (currentBlobUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(currentBlobUrlRef.current);
      currentBlobUrlRef.current = null;
    }
    setState({
      isApplying: false,
      colorizedUrl: null,
      lastTaskStatus: null,
      error: null,
    });
    lastAppliedKeyRef.current = "";
    prevEnabledRef.current = false;
  }, []);

  return {
    colorizedUrl: state.colorizedUrl,
    isApplying: state.isApplying,
    lastTaskStatus: state.lastTaskStatus,
    error: state.error,
    reset,
  };
};
